import { computed, ref } from "vue";
import axios from "axios";
import ApiHelper from "@/helpsers/api.helper";
import authHelper from "@/helpsers/auth.helper";

function normalize(data, key = "id") {
  return data.reduce((acc, val) => {
    acc[val[key]] = val;
    return acc;
  }, {});
}

export function useFurnitures(projectId) {
  async function fetchRooms() {
    const res = await axios.get(ApiHelper.getProjectDetailsUrl(projectId), {
      headers: authHelper.authHeader(),
    });
    return res.data.project_rooms;
  }
  async function fetchFurniture(roomName, roomId) {
    const res = await axios.get(ApiHelper.getFurnituresUrl(roomName), {
      headers: authHelper.authHeader(),
    });
    // mapped furnitures to match objects returned from project details
    const mappedFurnitures = res.data.map((item) => ({
      amount: 0,
      furnitureId: item.id,
      imagePath: item.imagePath,
      name: item.name_pl,
      slug: item.name,
      isActive: false,
      roomName: roomName,
      roomId: roomId,
    }));
    return [mappedFurnitures, roomId];
  }
  const loading = ref(true);
  const userRooms = ref({});
  const activeRoom = ref("");
  async function fetchClientRooms() {
    loading.value = true;
    try {
      const projectRooms = await fetchRooms();

      projectRooms.forEach((room, index) => {
        if (index === 0) {
          activeRoom.value = room.id + "";
        }
        room.room_furnitures = room.room_furnitures.map((furn) => ({
          ...furn,
          isActive: true,
        }));
      });
      const projectRoomsMap = normalize(projectRooms);

      const furniturePromises = projectRooms.map((projectRoom) => {
        const slug = projectRoom.room.slug;
        return fetchFurniture(slug, projectRoom.id);
      });
      const furnitureRes = await Promise.all(furniturePromises);
      furnitureRes.forEach(([furnitures, roomId]) => {
        const currentRoom = projectRoomsMap[roomId];
        const updatedAndSortedFurns = [];
        furnitures.forEach((furn, index) => {
          const hasThisRoom = currentRoom.room_furnitures.find(
            (f) => f.furnitureId === furn.furnitureId
          );
          if (hasThisRoom) {
            updatedAndSortedFurns[index] = hasThisRoom;
          } else {
            updatedAndSortedFurns[index] = furn;
          }
        });
        currentRoom.room_furnitures = updatedAndSortedFurns;
      });
      userRooms.value = projectRoomsMap;
    } catch (error) {
      console.log(error);
    } finally {
      loading.value = false;
    }
  }
  const activeFurniture = computed(() => {
    if (!userRooms.value?.[activeRoom.value]?.room_furnitures) {
      return {};
    }
    return userRooms.value[activeRoom.value].room_furnitures;
  });

  function mapRoomsWithFurnitureForRequest() {
    const rooms = [];
    for (const room of Object.values(userRooms.value)) {
      const furnitures = [];
      for (const furniture of room.room_furnitures) {
        if (furniture.isActive) {
          furnitures.push({
            furnitureId: furniture.furnitureId,
            amount:
              typeof furniture.amount === "number"
                ? furniture.amount
                : parseInt(furniture.amount),
          });
        }
      }
      rooms.push({ roomId: room.id, furnitures });
    }
    return rooms;
  }

  return {
    loading,
    fetchClientRooms,
    userRooms,
    activeRoom,
    activeFurniture,
    mapRoomsWithFurnitureForRequest,
  };
}
